import React, { useState } from 'react';
import { Row, Col } from 'antd';
import { FormikProps, Formik } from 'formik';
import { Form, Input, Select } from 'formik-antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import * as Yup from 'yup';

import Radio from 'components/formik/Radio';
import FormItem from 'components/formik/FormItem';
import InputNumber from 'components/formik/InputNumber';
import FormikEffect from 'components/formik/FormikEffect';
import { useAppSelector } from 'hooks/useAppSelector';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import {
  InvoicingCycleEnum,
  PayPeriodEnum,
  ResourceDownloadResponse,
  CommissionTypeEnum,
  PaymentTermEnum,
  CustomerCostingMethodTypeEnum,
} from 'api/backofficeApiClient';
import { PaymentMethodTypeEnum } from 'api/adminApiClient';
import {
  getCommissionTypeApprovalLabel,
  getCustomerInvoicingCycleOptions,
  getPaymentMethodTypeOptions,
  getCustomerPaymentTermsOptions,
  getCustomerPayrollCycleOptions,
} from 'utils/clientUtils';
import { urlRegex } from 'utils/urlUtils';
import {
  normalCharactersLength,
  mediumCharactersLength,
  assignmentRequirementLongTextMaxLength,
  dunsNumberMaxLength,
  payorNameMaxLength,
} from 'constants/index';
import PurchaseOrderUploader from './PurchaseOrderUploader';
import { getCustomerCostingMethodTypeLabel } from 'utils/clientUtils';

import styles from '../GeneralContractInfo/GeneralContractInfo.module.less';

type Props = {
  formRef: any;
  onChange: (formik: FormikProps<FinancialInfoFormValues>) => void;
  initialValues?: FinancialInfoFormValues;
  purchaseOrderFileInfo?: ResourceDownloadResponse;
};

export interface FinancialInfoFormValues {
  markupRate: string;
  dtBillRateMultiplier?: number | null;
  otRateMultiplier: string;
  MSPFee?: number | null | '';
  VMSFee?: number | null | '';
  invoicingInfo: string;
  invoicingInstructions: string;
  invoicingCycle: InvoicingCycleEnum | null;
  payrollCycle: PayPeriodEnum | null;
  isThirdWorkforce?: boolean;
  url?: string;
  providerName?: string;
  creditCheck: string;
  commissionType: CommissionTypeEnum | null;
  notes: string;
  paymentTerms: null | PaymentTermEnum;
  purchaseOrder?: boolean;
  purchaseOrderFileKey: string[];
  purchaseOrderInstructions?: string;
  customerCostingMethodType: CustomerCostingMethodTypeEnum | null;
  contractorConversionFees?: boolean;
  customerCreditNotes?: string;
  dunsNumber?: string;
  invoiceCutOffPeriod?: string;
  invoiceDisputePeriod?: string;
  paymentMethod?: PaymentMethodTypeEnum | null;
  payorName?: string;
}

const validationSchema = Yup.object({
  markupRate: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Fixed markup rate should be ${mediumCharactersLength} characters maximum`,
    ),
  dtBillRateMultiplier: Yup.number()
    .nullable()
    .min(0, 'DT bill rate multiplier fee should be from 0 to 100')
    .max(100, 'DT bill rate multiplier fee should be from 0 to 100'),
  otRateMultiplier: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `OT Rate multiplier should be ${mediumCharactersLength} characters maximum`,
    ),
  MSPFee: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' ? null : value,
    )
    .min(0, 'MSP fee should be from 0 to 100')
    .max(100, 'MSP fee should be from 0 to 100'),
  VMSFee: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' ? null : value,
    )
    .min(0, 'VMS fee should be from 0 to 100')
    .max(100, 'VMS fee should be from 0 to 100'),
  invoicingInfo: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Invoicing information should be ${mediumCharactersLength} characters maximum`,
    ),
  invoicingInstructions: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Special invoicing instructions should be ${mediumCharactersLength} characters maximum`,
    ),
  invoicingCycle: Yup.number().nullable(),
  payrollCycle: Yup.number().nullable(),
  isThirdWorkforce: Yup.boolean(),
  url: Yup.string()
    .matches(urlRegex, 'URL format is invalid')
    .max(
      mediumCharactersLength,
      `Url should be ${mediumCharactersLength} characters maximum`,
    ),
  providerName: Yup.string()
    .trim()
    .max(
      normalCharactersLength,
      `Provider name should be ${normalCharactersLength} characters maximum`,
    ),
  creditCheck: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Credit check score should be ${mediumCharactersLength} characters maximum`,
    ),
  commissionType: Yup.number().nullable(),
  notes: Yup.string()
    .trim()
    .max(
      mediumCharactersLength,
      `Financial special instructions should be ${mediumCharactersLength} characters maximum`,
    ),
  paymentTerms: Yup.number().nullable(),
  purchaseOrder: Yup.boolean(),
  purchaseOrderFileKey: Yup.array().of(Yup.string()),
  purchaseOrderInstructions: Yup.string().max(
    mediumCharactersLength,
    `Purchase order instructions should be ${mediumCharactersLength} characters maximum`,
  ),
  customerCostingMethodType: Yup.number().nullable(),
  contractorConversionFees: Yup.boolean(),
  customerCreditNotes: Yup.string()
    .trim()
    .max(
      assignmentRequirementLongTextMaxLength,
      `Customer credit notes should be ${assignmentRequirementLongTextMaxLength} characters maximum`,
    ),
  dunsNumber: Yup.string()
    .trim()
    .max(
      dunsNumberMaxLength,
      `D-U-N-S number should be ${dunsNumberMaxLength} characters maximum`,
    ),
  invoiceCutOffPeriod: Yup.string()
    .trim()
    .max(
      assignmentRequirementLongTextMaxLength,
      `Invoice cut off period should be ${assignmentRequirementLongTextMaxLength} characters maximum`,
    ),
  invoiceDisputePeriod: Yup.string()
    .trim()
    .max(
      assignmentRequirementLongTextMaxLength,
      `Invoice dispute period should be ${assignmentRequirementLongTextMaxLength} characters maximum`,
    ),
  paymentMethod: Yup.number().nullable(),
  payorName: Yup.string()
    .trim()
    .max(
      payorNameMaxLength,
      `Payor name should be ${payorNameMaxLength} characters maximum`,
    ),
});

export default function FinancialInfo(props: Props) {
  const {
    formRef,
    initialValues: predefinedInitialValues,
    onChange,
    purchaseOrderFileInfo,
  } = props;
  const [purchaseOrderFile, setPurchaseOrderFile] = useState(
    purchaseOrderFileInfo,
  );
  const isAdminRole = useAppSelector(isAdminSelector);

  const initialValues: FinancialInfoFormValues = {
    markupRate: '',
    dtBillRateMultiplier: null,
    otRateMultiplier: '',
    MSPFee: undefined,
    VMSFee: undefined,
    invoicingInfo: '',
    invoicingInstructions: '',
    invoicingCycle: null,
    payrollCycle: null,
    isThirdWorkforce: undefined,
    url: undefined,
    providerName: undefined,
    creditCheck: '',
    commissionType: null,
    notes: '',
    paymentTerms: null,
    purchaseOrder: undefined,
    purchaseOrderFileKey: [],
    purchaseOrderInstructions: '',
    customerCostingMethodType: null,
    contractorConversionFees: undefined,
    customerCreditNotes: '',
    dunsNumber: '',
    invoiceCutOffPeriod: '',
    invoiceDisputePeriod: '',
    paymentMethod: null,
    payorName: '',
  };

  const handleIsThirdWorkforce = (
    event: RadioChangeEvent,
    formik: FormikProps<FinancialInfoFormValues>,
  ) => {
    if (!event.target.value) {
      formik.setFieldValue('url', '', false);
      formik.setFieldValue('providerName', '', false);

      formik.setFieldTouched('url', false);
      formik.setFieldTouched('providerName', false);
    }
  };

  const handlePurchaseOrderChange = (
    ev: RadioChangeEvent,
    formik: FormikProps<FinancialInfoFormValues>,
  ) => {
    if (!ev.target.value) {
      formik.setFieldValue('purchaseOrderFileKey', [], false);
      formik.setFieldValue('purchaseOrderInstructions', '', false);
    }

    setPurchaseOrderFile(undefined);
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formRef}
      initialValues={predefinedInitialValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={() => {}}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />

          <Form layout="vertical">
            <div className="modal-title">Financial overview</div>

            <Row gutter={40}>
              <Col xs={24} md={12}>
                <FormItem
                  name="creditCheck"
                  label="Credit check score"
                  data-id="editCustomerInfoCreditCheckField"
                >
                  <Input
                    name="creditCheck"
                    placeholder="Enter credit check score"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  label="Funding type"
                  data-id="editCustomerInfoCommissionTypeField"
                  name="commissionType"
                >
                  <Radio.Group name="commissionType">
                    <Radio
                      name="selfFundedRadio"
                      value={CommissionTypeEnum.SelfFunded}
                      className={styles.radio}
                    >
                      {getCommissionTypeApprovalLabel(
                        CommissionTypeEnum.SelfFunded,
                      )}
                    </Radio>

                    <Radio
                      name="paidWhenPaidRadio"
                      value={CommissionTypeEnum.PaidWhenPaid}
                      className={styles.radio}
                    >
                      {getCommissionTypeApprovalLabel(
                        CommissionTypeEnum.PaidWhenPaid,
                      )}
                    </Radio>

                    <Radio
                      name="advancedCommissionRadio"
                      value={CommissionTypeEnum.AdvancedCommission}
                    >
                      {getCommissionTypeApprovalLabel(
                        CommissionTypeEnum.AdvancedCommission,
                      )}
                    </Radio>
                  </Radio.Group>
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="notes"
                  label="Financial special instructions"
                  data-id="editCustomerInfoNotesField"
                >
                  <Input
                    name="notes"
                    placeholder="Enter financial special instructions"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem name="paymentTerms" label="Payment terms">
                  <Select
                    name="paymentTerms"
                    placeholder="Select payment terms"
                  >
                    {getCustomerPaymentTermsOptions().map((term) => (
                      <Select.Option
                        key={`select-payment-term-${term.value}`}
                        value={term.value || ''}
                      >
                        {term.label}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="customerCostingMethodType"
                  label="Customer costing method type"
                >
                  <Select
                    name="customerCostingMethodType"
                    placeholder="Select customer costing method type"
                  >
                    <Select.Option
                      value={CustomerCostingMethodTypeEnum.Blended}
                    >
                      {getCustomerCostingMethodTypeLabel(
                        CustomerCostingMethodTypeEnum.Blended,
                      )}
                    </Select.Option>
                    <Select.Option
                      value={CustomerCostingMethodTypeEnum.CostPlus}
                    >
                      {getCustomerCostingMethodTypeLabel(
                        CustomerCostingMethodTypeEnum.CostPlus,
                      )}
                    </Select.Option>
                  </Select>
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="contractorConversionFees"
                  label="Contractor conversion fees?"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="contractorConversionFees"
                    options={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ]}
                  />
                </FormItem>
              </Col>

              {isAdminRole && (
                <Col xs={24} md={12}>
                  <FormItem
                    name="customerCreditNotes"
                    label="Customer credit notes"
                  >
                    <Input.TextArea
                      name="customerCreditNotes"
                      placeholder="Enter customer credit notes"
                      autoComplete="off"
                      rows={4}
                    />
                  </FormItem>
                </Col>
              )}

              {isAdminRole && (
                <Col xs={24} md={12}>
                  <FormItem name="dunsNumber" label="D-U-N-S number">
                    <Input
                      name="dunsNumber"
                      placeholder="Enter D-U-N-S number"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>
              )}

              <Col xs={24} md={12}>
                <FormItem
                  name="invoicingCycle"
                  label="Invoicing cycle"
                  data-id="editCustomerInvoicingCycleField"
                >
                  <Select
                    allowClear
                    name="invoicingCycle"
                    placeholder="Select invoicing cycle"
                  >
                    {getCustomerInvoicingCycleOptions().map((invoicingCycle) => (
                      <Select.Option
                        key={`select-invoicing-cycle-${invoicingCycle.value}`}
                        value={invoicingCycle.value || ''}
                      >
                        {invoicingCycle.label}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="invoicingInfo"
                  label="Invoicing information"
                  data-id="editCustomerInvoicingInfoField"
                >
                  <Input
                    name="invoicingInfo"
                    placeholder="Enter invoicing information"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="invoicingInstructions"
                  label="Special invoicing instructions"
                  data-id="editCustomerInvoicingInstructionsField"
                >
                  <Input
                    name="invoicingInstructions"
                    placeholder="Enter special invoicing instructions"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="payrollCycle"
                  label="Pay period"
                  data-id="editCustomerPayrollCycleField"
                >
                  <Select
                    allowClear
                    name="payrollCycle"
                    placeholder="Select pay period"
                  >
                    {getCustomerPayrollCycleOptions().map((payrollCycle) => (
                      <Select.Option
                        key={`select-payroll-cycle-${payrollCycle.value}`}
                        value={payrollCycle.value || ''}
                      >
                        {payrollCycle.label}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="purchaseOrder"
                  label="Purchase order"
                  data-id="editCustomerPurchaseOrderField"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="purchaseOrder"
                    onChange={(ev) => handlePurchaseOrderChange(ev, formik)}
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                  />
                </FormItem>
              </Col>

              {formik.values.purchaseOrder && (
                <>
                  <Col xs={24} md={12}>
                    <FormItem
                      name="purchaseOrderInstructions"
                      label="Purchase order instructions"
                    >
                      <Input
                        name="purchaseOrderInstructions"
                        placeholder="Enter purchase order instructions"
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>
                  <Col xs={24} md={12}>
                    <div className="upload-wrapper">
                      <FormItem
                        name="purchaseOrderFileKey"
                        label="Purchase order"
                        data-id="editCustomerPurchaseOrderFileFileField"
                        showValidateError={false}
                      >
                        <PurchaseOrderUploader
                          fieldName="purchaseOrderFileKey"
                          file={purchaseOrderFile}
                        />
                      </FormItem>
                    </div>
                  </Col>
                </>
              )}

              <Col xs={24} md={12}>
                <FormItem
                  name="invoiceCutOffPeriod"
                  label="Invoice cut off period"
                >
                  <Input.TextArea
                    name="invoiceCutOffPeriod"
                    placeholder="Enter invoice cut off period"
                    autoComplete="off"
                    rows={4}
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="invoiceDisputePeriod"
                  label="Invoice dispute period"
                >
                  <Input.TextArea
                    name="invoiceDisputePeriod"
                    placeholder="Enter invoice dispute period"
                    autoComplete="off"
                    rows={4}
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem name="paymentMethod" label="Payment method">
                  <Select
                    allowClear
                    name="paymentMethod"
                    placeholder="Select payment method"
                  >
                    {getPaymentMethodTypeOptions().map((opt) => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Select.Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem name="payorName" label="Payor name">
                  <Input
                    name="payorName"
                    placeholder="Enter payor name"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>
            </Row>

            <div className="modal-title">Rate overview</div>

            <Row gutter={40}>
              <Col xs={24} md={12}>
                <FormItem
                  name="dtBillRateMultiplier"
                  label="DT bill rate multiplier"
                  data-id="editCustomerInfoDtBillRateMultiplierField"
                >
                  <InputNumber
                    name="dtBillRateMultiplier"
                    placeholder="0.00"
                    precision={2}
                  />
                </FormItem>
              </Col>
              <Col xs={24} md={12}>
                <FormItem
                  name="markupRate"
                  label="Fixed markup rate"
                  data-id="editCustomerInfoMarkupRateField"
                >
                  <Input
                    name="markupRate"
                    placeholder="Enter fixed markup rate"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="otRateMultiplier"
                  label="OT Rate multiplier"
                  data-id="editCustomerInfoOtRateMultiplierField"
                >
                  <Input
                    name="otRateMultiplier"
                    placeholder="Enter OT Rate multiplier"
                    autoComplete="off"
                  />
                </FormItem>
              </Col>
            </Row>

            <div className="modal-title">MSP/VMS overview</div>

            <Row gutter={40}>
              <Col xs={24} md={12}>
                <FormItem
                  name="MSPFee"
                  label="MSP fee"
                  data-id="editCustomerMSPFeeField"
                >
                  <InputNumber
                    name="MSPFee"
                    placeholder="Enter MSP fee %"
                    precision={2}
                    emptyValueAsString
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="VMSFee"
                  label="VMS fee"
                  data-id="editCustomerVMSFeeField"
                >
                  <InputNumber
                    name="VMSFee"
                    placeholder="Enter VMS fee %"
                    precision={2}
                    emptyValueAsString
                  />
                </FormItem>
              </Col>

              <Col xs={24} md={12}>
                <FormItem
                  name="isThirdWorkforce"
                  label="Is there an MSP or VMS?"
                  className="inline-radio"
                >
                  <Radio.Group
                    className="radio"
                    name="isThirdWorkforce"
                    onChange={(ev) => handleIsThirdWorkforce(ev, formik)}
                    options={[
                      {
                        label: 'Yes',
                        value: true,
                      },
                      {
                        label: 'No',
                        value: false,
                      },
                    ]}
                  />
                </FormItem>
              </Col>

              {formik.values.isThirdWorkforce && (
                <>
                  <Col xs={24} md={12}>
                    <FormItem name="url" label="URL">
                      <Input
                        name="url"
                        placeholder="Enter URL"
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>

                  <Col xs={24} md={12}>
                    <FormItem name="providerName" label="MSP/VMS provider name">
                      <Input
                        name="providerName"
                        placeholder="Enter MSP/VMS provider name"
                        autoComplete="off"
                      />
                    </FormItem>
                  </Col>
                </>
              )}

            </Row>
          </Form>
        </>
      )}
    </Formik>
  );
}
